import React from 'react';
import { useDrop } from 'react-dnd';
import { Button } from './ui/button';
import { Element as ElementComponent } from './index';
import { Element as ElementType, MachineType, MachineState } from '../types';
import { Loader2, Lock } from 'lucide-react';
import { MdCleaningServices } from 'react-icons/md';
import { FaStar } from 'react-icons/fa';
import MachineSlot from './MachineSlot';
import './animations.css';
import { useInteractionMode } from '../contexts/InteractionModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useGameState } from '@/contexts/GameStateContext';
import CombinationWarning from './CombinationWarning';

interface MachineProps {
  type: MachineType;
  state: MachineState;
  onDrop: (slot: 'input1' | 'input2', element: ElementType) => void;
  onCombine: () => Promise<void>;
  onClear: () => void;
  onOutputDragEnd: () => void;
  newDiscoveries?: Record<string, boolean>;
  locked?: boolean;
}

const Machine: React.FC<MachineProps> = ({
  type,
  state,
  onDrop,
  onCombine,
  onClear,
  onOutputDragEnd,
  newDiscoveries = {},
  locked = false,
}) => {
  // Get interaction mode from context
  const { mode, selectedElement, setSelectedElement } = useInteractionMode();

  // Get combinations from game state
  const { hasCombination } = useGameState();

  // Check if the current combination has been tried before
  const isCombinationTried = React.useMemo(() => {
    if (state.input1 && state.input2) {
      return hasCombination(state.input1.name, state.input2.name, type);
    }
    return false;
  }, [state.input1, state.input2, hasCombination, type]);

  // Handle tap destination for input slots
  const handleTapInput1 = () => {
    if (mode === 'tap-to-location' && selectedElement) {
      onDrop('input1', selectedElement);
      setSelectedElement(null);
    }
  };

  const handleTapInput2 = () => {
    if (mode === 'tap-to-location' && selectedElement) {
      onDrop('input2', selectedElement);
      setSelectedElement(null);
    }
  };

  const [{ isOver: isOverInput1 }, dropInput1] = useDrop({
    accept: 'element',
    drop: (item: ElementType) => {
      onDrop('input1', item);
      return { handled: true, name: `Machine-${type}-Input1` };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const [{ isOver: isOverInput2 }, dropInput2] = useDrop({
    accept: 'element',
    drop: (item: ElementType) => {
      onDrop('input2', item);
      return { handled: true, name: `Machine-${type}-Input2` };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const canCombine = state.input1 !== null && state.input2 !== null && !state.isProcessing;

  return (
    <div className={`machine relative ${locked ? 'opacity-80' : ''}`}>
      {/* Gold star for new elements - positioned at the root level */}
      {state.output && newDiscoveries && newDiscoveries[state.output.id] === true && (
        <div className="absolute bottom-[10px] right-[5px] z-50">
          <div className="animate-rock">
            <FaStar className="text-yellow-400 text-4xl drop-shadow-lg" />
          </div>
        </div>
      )}

      {/* Lock overlay for locked machines */}
      {locked && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-xl border border-border">
          <Lock className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-bold mb-2">{type} Forge Locked</h3>
          <p className="text-sm text-muted-foreground text-center px-4 mb-4">
            {type === 'Location' ? 'Sign in to unlock' : 'Purchase actions to unlock'}
          </p>
        </div>
      )}

      <div className="flex justify-between items-center mb-1">
        <h3 className="text-base font-bold">{type} Forge</h3>
        <div className="flex items-center gap-1">
          {/* Show warning icon if combination has been tried before */}
          {isCombinationTried && canCombine && <CombinationWarning className="mr-1" />}
          <Button
            onClick={onCombine}
            disabled={!canCombine || locked}
            className="h-6 px-2"
            size="sm"
          >
            {state.isProcessing ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Combining...
              </>
            ) : (
              'Combine'
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClear}
            title="Clear machine"
            className="h-6 w-6"
            disabled={locked}
          >
            <MdCleaningServices className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <div
          ref={dropInput1}
          onClick={handleTapInput1}
          className={`machine-area ${isOverInput1 ? 'droppable-active' : ''} ${
            mode === 'tap-to-location' && selectedElement ? 'cursor-pointer hover:bg-accent/50' : ''
          }`}
          style={{ minHeight: '40px', maxHeight: '50px', overflow: 'hidden' }}
        >
          {state.input1 ? (
            <MachineSlot element={state.input1} slotType="input" />
          ) : (
            <p className="text-muted-foreground text-center text-xs">
              {mode === 'tap-to-location' && selectedElement ? 'Tap to place' : 'Drop first'}
            </p>
          )}
        </div>

        <div
          ref={dropInput2}
          onClick={handleTapInput2}
          className={`machine-area ${isOverInput2 ? 'droppable-active' : ''} ${
            mode === 'tap-to-location' && selectedElement ? 'cursor-pointer hover:bg-accent/50' : ''
          }`}
          style={{ minHeight: '40px', maxHeight: '50px', overflow: 'hidden' }}
        >
          {state.input2 ? (
            <MachineSlot element={state.input2} slotType="input" />
          ) : (
            <p className="text-muted-foreground text-center text-xs">
              {mode === 'tap-to-location' && selectedElement ? 'Tap to place' : 'Drop second'}
            </p>
          )}
        </div>
      </div>

      <div
        className="machine-area output-area relative"
        style={{ minHeight: '40px', maxHeight: '50px', overflow: 'hidden' }}
      >
        {state.output ? (
          <MachineSlot
            element={state.output}
            slotType="output"
            onDragEnd={onOutputDragEnd}
            // We're now handling the star at the container level
            isNew={false}
          />
        ) : (
          <p className="text-muted-foreground text-center text-xs">Output will appear here</p>
        )}
      </div>
    </div>
  );
};

export default Machine;
